// import store from "../storage";
import axios from "axios";
import { LANGUAGES, USER_TYPE } from "../constants/constants";
import i18n from "../util/i18n";
import { getUserImage } from "../store/actions/notification";

export const updateObject = (oldObject: any, updatedProperties: any) => {
  return {
    ...oldObject,
    ...updatedProperties,
  };
};

export const isEmpty = (item: any) => {
  if (typeof item === "string") {
    if (item.length === 0) return true;
    if (item.length !== 0) return false;
  }
  if (!item || typeof item !== "object") return true;
  if (Array.isArray(item)) {
    return item.length > 0 ? false : true;
  }
  return !Object.keys(item).length;
};

export const getErrors = (err: any) => {
  let error = "Something went wrong. Please try later";

  if (err) error = err;
  if (err.error) error = err.error;
  if (err.message) error = err.message;
  if (err?.response?.data?.message) error = err?.response?.data?.message;

  return error;
};

export const transformErrorsFromAPI = (
  err: { [s: string]: unknown } | ArrayLike<unknown>
) => {
  const errors: any = {};

  if (typeof err === "string") {
    errors["error"] = err;
    return errors;
  }

  for (const [key, value] of Object.entries(err)) {
    if (key === "error") {
      errors["error"] = value;
    } else {
      errors[key] = value;
    }
  }

  return errors;
};

export const getResData = (
  res: {
    toString: () => null;
    result: string | null;
    errorCode: number;
    debugInfo: any;
    errorDesc: any;
    message?: string;
    data: {
      result: string | null;
      debugInfo: any;
      errorDesc: any;
      data: null;
      total: null;
      errorCode?: number;
    } | null;
    total: null;
  } | null,
  inCatch = false
) => {
  const responseData: any = {
    success: false,
    result: null,
    total: null,
    data: null,
    error: null,
    errorCode: null,
  };

  if (inCatch) {
    if (res && typeof res === "object" && isEmpty(res)) {
      responseData.error = res.toString();
    } else {
      responseData.error = res;
    }
    return responseData;
  }
  if (isEmpty(res)) return responseData;

  //   if (res?.result === "error" && res.errorCode === 102) store.dispatch(logout())

  if (res?.result === "error") {
    responseData.error = res.debugInfo || res.errorDesc;
    if (res?.message) {
      responseData.error = res?.message;
    }
    responseData.errorCode = res?.errorCode;
    return responseData;
  }
  if (res?.data && res.data.result === "error") {
    responseData.error = res.data.debugInfo || res.data.errorDesc;
    return responseData;
  }

  if (res?.result === "ok") {
    responseData.success = true;
    responseData.result = res.result;
    responseData.data = res.data;
    if (res.total) {
      responseData.total = res.total;
    }
    return responseData;
  }
  if (res?.data && res.data.data && res.data.result) {
    responseData.success = true;
    responseData.result = res.data.result;
    responseData.data = res.data.data;
    if (res.data.total) {
      responseData.total = res.data.total;
    }
    return responseData;
  }
  return responseData;
};

export const arrayBufferToBase64 = (buffer: Iterable<number>) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

export const getTotalAmount = (
  price: number,
  pricePeriod: string,
  rentFrom: string | number | Date,
  rentTo: string | number | Date
) => {
  let totalPrice: number = 0;
  const from: any = new Date(rentFrom);
  const to: any = new Date(rentTo);

  if (pricePeriod === "Day") {
    const daysDifference = Math.ceil((to - from + 1) / (1000 * 60 * 60 * 24));
    totalPrice += price * daysDifference;
  } else if (pricePeriod === "Month") {
    const monthsDifference =
      (to.getFullYear() - from.getFullYear()) * 12 +
      (to.getMonth() - from.getMonth()) +
      1;
    totalPrice += price * monthsDifference;
  }

  return totalPrice;
};

export const formatName = (name: string) => {
  let formatedName: string = "";
  let nameArr: string[];
  nameArr = name?.split(" ");
  nameArr?.forEach((e, i) => {
    formatedName = formatedName.concat(
      (i > 0 ? " " : "") + e.charAt(0).toUpperCase() + e.slice(1)
    );
  });
  return formatedName;
};

/* Currently, we are getting images from API responses with an incorrect base64 prefix format. To fix the issue... */
export const formatBase64Image = (base64: string | any = '') => {
  if (base64 && (typeof base64 === "string")) {
    // Combined Case 1 and Case 2 regex
    const regex = /^data:image\/(.*);base64,|^dataimage\/(.*)base64/;

    if (regex.test(base64)) {
      return base64.replace(regex, "data:image/jpg;base64,");
    }

    // Case 3: No prefix
    return "data:image/jpg;base64," + base64;
  }

  return "";
};

export const optionValues = (values: any, isId: boolean = false) => {
  return isId
    ? values?.map((e: any) => ({ value: e.id, label: e.name }))
    : values?.map((e: any) => ({ value: e === LANGUAGES.ENGLISH.label ? LANGUAGES.ENGLISH.value : e === LANGUAGES.AMHARIC.label ? LANGUAGES.AMHARIC.value : e === LANGUAGES.OROMO.label ? LANGUAGES.OROMO.value : '' , label: e }));
};

export const fileToBase64 = (file: File) => {
  return new Promise((resolve, reject) => {
    if (file) {
      if (file?.size > 1024 * 1024) { // check image size if more than 1mb
        reject(translationProvider("Notification.ERROR.FILE_SIZE_1MB"));
      } else {
        const reader = new FileReader();
        
        reader.readAsDataURL(file);
        
        reader.onload = () => {
          resolve(reader.result);
        };
        
        reader.onerror = (error) => {
          reject(error);
        };
      }
    } else {
      reject(new Error(translationProvider("Notification.ERROR.FILE_NOT_FOUND")));
    }
  });
};

export const formatDate = (dateString: string | number | Date) => {
  // Create a new Date object from the given string
  const date = new Date(dateString);

  // Extract year, month, and day components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, "0");

  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

// Get user locations using google API 
export const getCityFromLatLng = async (lat:any, lng:any, apiKey:any) => {
  try {
    const response = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
    );

    const addressComponents = response.data.results[0].address_components;
    const cityComponent = addressComponents.find((component:any) =>
      component.types.includes('locality')
    );

    return cityComponent ? cityComponent.long_name : 'Unknown City';
  } catch (error) {
    console.error('Error fetching city name:', error);
    return 'Unknown City';
  }
};

// fetch browser's location
export const handleGeoLocation = (): Promise<{ lat: number, lng: number }> => {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        resolve({ lat: latitude, lng: longitude });
      },
      (error) => {
        console.error('Error getting user location:', error);
        reject(error);
      }
    );
  });
};

// function to verify image or not
export const handleImageVerification = (
  base64String: string | ArrayBuffer | null
): boolean => {
  if (typeof base64String === "string") {
    const mimeRegex = /^data:(image\/[a-zA-Z+]*);base64,/; // Regex to match image type

    // match base64
    const mimeTypeMatch = base64String.match(mimeRegex);

    // If a match is found and it's an image type, return true
    return !!mimeTypeMatch && mimeTypeMatch[1].startsWith("image/");
  } else {
    return false;
  }
};

// Function for scrolling to the top of the page
export const scrollToTop = () => {
  setTimeout(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, 200);
};

export const getUserCurrentLanguage = () => {
  return i18n.languages[0]  === LANGUAGES.ENGLISH.value
  ? LANGUAGES.ENGLISH.label
  : i18n.languages[0]  === LANGUAGES.AMHARIC.value
  ? LANGUAGES.AMHARIC.label
  : i18n.languages[0]  === LANGUAGES.OROMO.value
  ? LANGUAGES.OROMO.label
  : "";
}

export const decodeEmoji = (message: string) => {
  if(!message) return message;
  return message.replace(/\\u[\dA-Fa-f]{4}/g, (match) => {
    const codePoint = parseInt(match.substring(2), 16);
    return String.fromCodePoint(codePoint);
 });
};

export const encodeEmoji = (text: string): string => {
  // Define the regular expression to match emojis
  const regex = /[\u{1F000}-\u{1FFFF}]/gu;

  let emojiUnicodes = '';
  let i;
  let matchedText = text.match(regex);
  if (matchedText !== null) {
      let unicodes = matchedText
          .map(e => "\\u" + e.charCodeAt(0).toString(16) + "\\u" + e.charCodeAt(1).toString(16));
      for (i = 0; i < unicodes.length; i++) {
          emojiUnicodes = emojiUnicodes + unicodes[i];
      }
      // eslint-disable-next-line no-useless-escape
      text = text.replace(regex, '') + emojiUnicodes.replace(/\\"/g, '\"');
  }
  return text;
};

export const extractFileExtension = (base64Prefix: string): string => {
  const matches = base64Prefix.match(/data:.*?\/.*?(?:\+.*?;)?base64/g);
  if (matches && matches.length > 0) {
      const mimeType = matches[0].split("/")[1].split(";")[0];

      switch (mimeType) {
        case "plain":
            return ".txt";
        case "pdf":
            return ".pdf";
        case "jpeg":
        case "jpg":
            return ".jpg";
        case "png":
            return ".png";
        case "gif":
            return ".gif";
        case "svg+xml":
            return ".svg";
        case "html":
            return ".html";
        case "xml":
            return ".xml";
        case "css":
            return ".css";
        case "javascript":
            return ".js";
        case "vnd.openxmlformats-officedocument.wordprocessingml.document":
            return ".docx";
        case "vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            return ".xlsx";
        case "vnd.openxmlformats-officedocument.presentationml.presentation":
            return ".pptx";
        case "vnd.ms-excel":
            return ".xls";
        case "vnd.ms-powerpoint":
            return ".ppt";
        case "vnd.ms-word":
        case "msword":
            return ".doc";
        case "x+m4a":
            return ".m4a";
        case "zip":
            return ".zip";
        case "gzip":
            return ".gz";
        case "octet-stream":
            return ".bin";
        case "mp3":
            return ".mp3";
        case "mp4":
            return ".mp4";
        default:
            return ".dat";
    }    
  }
  return ".dat";
};

// Capitalizes the first letter of a sentence and converts the rest to lowercase.
export const handleCapitalizeFirstLetter = (word: string | null) => {
  if(!word) return "";
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

// to convert currency in to international currency format (ex: 2555666.99 => 2,555,666.99)
export const currencyFormat = (value: any) => {
  if(!value){
    return value;
  }

  if( typeof value !== 'string'){
    value = value.toString();
  }

  const [integerPart, decimalPart = ''] = value.split('.');

  let result = integerPart.trim().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if(decimalPart){
    result = result + '.' + decimalPart;
  }

  return result;
}

// to get other users profile picture
export const getSenderImage = async (msisdn:string, setSenderImage: any,) => {
  const imageResponse = await getUserImage(msisdn);
  if(imageResponse?.success && imageResponse?.data) {
      setSenderImage(formatBase64Image(imageResponse?.data));
  }
}

// To know how many product cards going to be in a page based on screen size.
export const getProductCardsCountPerPage = () => {
  const screenWidth: number = document.body.clientWidth;
    let cards = 1;
    switch(true){
      case (screenWidth >= 1532) : 
            cards = 4;
            break;
      case (screenWidth >= 1230) : 
            cards = 3;
            break;
      case (screenWidth >= 920) : 
            cards = 2;
            break;
      case (screenWidth >= 300) : 
            cards = 1;
            break;
      default: cards = 4;
    }

    return cards;
}

// To handle products card slider ( react-slick library )
export const handleReactSlickSliderArrowButtons = (current: number, itemsCount: number, setIsDisablePrevious: any, setIsDisableNext: any, ) => {
  let cards = getProductCardsCountPerPage();
  const upperLimit = itemsCount - cards;
  
  setIsDisablePrevious(current < 1);
  setIsDisableNext(current >= upperLimit);
};

// to use translation outside any 'component'
export const translationProvider = (key: string): string => {
  return i18n.t(key);
}

// Optimizing the File size for displaying in UI
export const formatFileSizeForUI = (bytes: number) => {
  if (bytes < 1024) {
    return bytes + ' bytes';
  } else if (bytes < 1024 * 1024) {
    return Math.ceil(bytes / 1024) + ' KB';
  } else {
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }
};

// Optimizing the File lastModifiedDate for displaying in UI ( service provider >> 'Add your certificate and ID' modal )
// export const formatFileDateForUI = (lastModifiedDate: any) => {
//   //lastModifiedDate is a 'date object'. So, we are converting it to string
//   if(typeof lastModifiedDate !== "string"){
//     lastModifiedDate = lastModifiedDate.toString();
//   }

//   const [weekday = "", month = "00", day = "00", year ="0000"] = lastModifiedDate.split(" ");
//   return `${day} ${month}, ${year}`;
// };

/* To support mobile devices */
export const formatFileDateForUI = (lastModifiedDate: any): string => {
  // Ensure lastModifiedDate is a Date object
  if (!(lastModifiedDate instanceof Date)) {
    return "00 00, 0000";
  }

  // Use toLocaleDateString for consistent formatting
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
  const dateStr: string = lastModifiedDate.toLocaleDateString('en-US', options);

  // Handle different locale formats by ensuring the split parts are correct
  const dateParts = dateStr.split(' ');

  // Some locales might have a different order or separator, handle common cases
  let day, month, year;
  if (dateParts.length === 3) {
    [month, day, year] = dateParts;
  } else {
    // Fallback for unexpected formats
    return "00 00, 0000";
  }

  return `${day.replace(',', '')} ${month.replace('.', '')}, ${year}`;
};

/* To get the Language Object */
export const getLanguageObject = (language: string) => {
  if(typeof language !== "string"){
    return { label: "", value: "" };
  }

  switch(true){
      case ( language === LANGUAGES.ENGLISH.value):
          return LANGUAGES.ENGLISH;
      case ( language === LANGUAGES.AMHARIC.value):
          return LANGUAGES.AMHARIC;
      case ( language === LANGUAGES.OROMO.value):
          return LANGUAGES.OROMO;
      default: return { label: language, value: language };
  }
};

export const base64ToFile = (base64String: string, fileName: string): any => {
  try {
      // Split the base64 string to get the data part
      const byteString = atob(base64String.split(',')[1]);
      
      // Get the mime type from the base64 string
      const mimeType = base64String.match(/data:([^;]+);/)?.[1] || '';

      // Create an ArrayBuffer and a Uint8Array to hold the binary data
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      
      // Convert the base64 string to binary data
      for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
      }

      // Create a Blob from the binary data
      const blob = new Blob([ab], { type: mimeType });

      // Return a File from the Blob
      return new File([blob], fileName, { type: mimeType });
  } catch (error) {
      console.error('Error converting base64 to file:', error);
      return null;
  }
};

/* Function to add a base64 prefix to a string if it does not already contain one. */
export const addBase64Prefix = (base64String: string, defaultPrefix: string = "data:image/jpg;base64,", ) => {
  if (!base64String?.startsWith("data:")) {
      return defaultPrefix + base64String;
  }
  return base64String;
};

/* Function to display the profile type in the UI in a user-friendly format. Currently, it returns the translation key. */
export const profileTypeForUi = (profile: string) => {
  switch(profile){
    case USER_TYPE.SELLER : return "sign_up_3";
    case USER_TYPE.BUYER : return "sign_up_7";
    case USER_TYPE.PROVIDER : return "sign_up_70";
    default : return "";
  }
};

/* Function to download a file from a Base64 string */
export const downloadFileFromBase64 = (base64: string, fileName: string = "file", fileExtension: string = "",) => {
  let a = document.createElement("a");
  a.href = base64;

  if(!fileExtension){
    const index = base64.indexOf(",");
    const basePrefix = base64.substr(0, index+1);

    fileExtension = extractFileExtension(basePrefix || "");
  }

  a.download = `${fileName}${fileExtension}`;
  a.click();
};

/* Remove this function: It has been replaced by "formatBase64Image" function. */
// /* Function to Convert a Base64 String to an Image */
// export const base64toImage = (base64: string) : string => {
//   if(!base64 || typeof base64 !== "string"){
//     return "";
//   }

//   if(base64.includes('dataimage')){
//     return base64;
//   } else {
//     return 'data:image/jpeg;base64,' + base64;
//   }
// };

/*
  In the chat, we use sockets to send messages.
  When we send a message, we want it to appear in the UI without making an additional API call.
  Therefore, we mimicking the date received in the API response "user/message/conversation".
  Format => "DD-MM-YYYY HH:MM:SS"
*/
export const currentDateInSocketResponseFormat= () : string => {
  const currentDate: Date = new Date();

  const day: number = currentDate.getDate();
  const month: number = currentDate.getMonth() + 1; // Months are zero-indexed
  const year: number = currentDate.getFullYear();
  const hours: number = currentDate.getHours();
  const minutes: number = currentDate.getMinutes();
  const seconds: number = currentDate.getSeconds();

  // Return formatted date and time as "DD-MM-YYYY HH:MM:SS"
  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
}

/*
  The below code is being implemented to achieve a feature:
  When a user navigates back to the "landing page" from the "product details page",
  the previously selected categories (filters) will be retained in the "Our Best Seller Products" and "New Products" sections.
  Otherwise, the filters will be set to default.
*/
export const updateNavigationDetails = (currentLocation: string = "/") => {
  const previousNavigationDetails = JSON?.parse(sessionStorage?.getItem("m-market-navigation-details") || "null") || {};

  const currentLocationsDetails = {
    current: currentLocation,
    prev: previousNavigationDetails?.locations?.current || '',
    prevPrev: previousNavigationDetails?.locations?.prev || '',
  };

  const currentFiltersDetails =
    (currentLocationsDetails?.current !== '/') ? // If current location is not the landing page
    (previousNavigationDetails?.filters || {}) // Keep the previous filters
    : (
      (currentLocationsDetails?.current === currentLocationsDetails?.prevPrev) // If navigating back to home page from a product page
      && currentLocationsDetails?.prev?.includes("/product-details/") // Check if the previous location was a product details page
    ) ?
    (previousNavigationDetails?.filters || {}) : {};

  const currentNavigationDetails = {
    ...previousNavigationDetails,
    filters: currentFiltersDetails,
    locations: currentLocationsDetails,
  };

  sessionStorage.setItem("m-market-navigation-details", JSON.stringify(currentNavigationDetails));
};

/* Removes all non-numeric characters from the input string, leaving only digits. */
export const filterNumericInput = (value : string): string => {
  const newValue = value.replace(/[^0-9]/g, '');
  return newValue;
};